import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import Button from '../components/Button'
import WellnessScoreQuestion from '../components/WellnessScoreQuestion'
import { WELLNESS_CHECK_IN_SCORES } from '../constants/wellnessCheckInScores'
import api from '../services/api'
import { saveAuth, getPendingCheckIns } from '../utils/userData'
import { API_ENDPOINTS, ROUTES, COHORT_DISPLAY_NAMES } from '../utils/constants'
import { trackEvent, identifyUser } from '../utils/mixpanel'

const CohortSignup = ({ programSlug = 'build3-cohort-7' }) => {
  const cohortDisplayName = COHORT_DISPLAY_NAMES[programSlug] || 'Build3 Energizer Sessions'

  const navigate = useNavigate()
  const location = useLocation()
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [signupId, setSignupId] = useState(null)
  const [verificationSent, setVerificationSent] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    yogaExperience: '',
    energyLevel: null,
    stressLevel: null,
    emotionalState: null,
    physicalState: null,
    wellnessGoal: '',
  })

  // OTP related state
  const [otp, setOtp] = useState(['', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [devCode, setDevCode] = useState('')
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)]

  const [errors, setErrors] = useState({})

  // Handle prefilled data from logged-in user navigation
  useEffect(() => {
    if (location.state?.prefillEmail) {
      setFormData((prev) => ({
        ...prev,
        email: location.state.prefillEmail,
        ...(location.state.prefillName && { name: location.state.prefillName }),
        ...(location.state.prefillPhone && { phone: location.state.prefillPhone }),
      }))
    }
  }, [location.state])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Focus first OTP input when verification step is shown
  useEffect(() => {
    if (currentStep === steps.length - 1 && otpRefs[0].current) {
      setTimeout(() => otpRefs[0].current?.focus(), 300)
    }
  }, [currentStep])

  // Check for pending check-ins
  const pendingCheckIns = getPendingCheckIns()
  const hasPendingCheckIns = pendingCheckIns.length > 0

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Name is required'
    if (name.trim().length < 2) return 'Name must be at least 2 characters'
    return ''
  }

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return 'Please enter a valid email address'
    return ''
  }

  const validatePhone = (phone) => {
    if (!phone.trim()) return 'Phone number is required'
    const phoneRegex = /^[\d\s\-+()]{10,}$/
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number'
    return ''
  }

  const validateGender = (gender) => {
    if (!gender.trim()) return 'Please select your gender'
    return ''
  }

  const validateExperience = (exp) => {
    if (!exp.trim()) return 'Please select your experience level'
    return ''
  }

  const validateWellnessScores = () => {
    const scores = [
      { key: 'energyLevel', label: 'Energy Level' },
      { key: 'stressLevel', label: 'Stress Level' },
      { key: 'emotionalState', label: 'Emotional State' },
      { key: 'physicalState', label: 'Physical State' },
    ]
    for (const { key, label } of scores) {
      const value = formData[key]
      if (value === null || value === undefined) {
        return `Please select your ${label}`
      }
      if (value < 1 || value > 10) {
        return `${label} must be between 1 and 10`
      }
    }
    return ''
  }

  const validateGoal = (goal) => {
    if (!goal.trim()) return 'Please select a wellness goal'
    return ''
  }

  // Experience options with emojis and descriptions
  const experienceOptions = [
    { value: 'Beginner', label: 'Beginner', emoji: '🌱', description: 'New to yoga and mindfulness' },
    { value: 'Intermediate', label: 'Intermediate', emoji: '🌿', description: 'Some experience with regular practice' },
    { value: 'Advanced', label: 'Advanced', emoji: '🌳', description: 'Strong foundation with years of practice' },
    { value: 'Expert', label: 'Expert', emoji: '🧘', description: 'Mastery level with deep understanding' },
  ]

  // Gender options
  const genderOptions = [
    { value: '', label: 'Select your gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' },
  ]

  // Wellness goal options
  const goalOptions = [
    { value: 'stress-relief', label: 'Stress Relief' },
    { value: 'physical-fitness', label: 'Physical Fitness' },
    { value: 'mental-clarity', label: 'Mental Clarity' },
    { value: 'spiritual-growth', label: 'Spiritual Growth' },
    { value: 'stable-energy-levels', label: 'Stable Energy Levels' },
    { value: 'better-sleep', label: 'Better Sleep' },
    { value: 'overall-wellness', label: 'Overall Wellness' },
  ]

  // Step configuration
  const steps = [
    {
      id: 'welcome',
      title: cohortDisplayName,
      subtitle: 'Sign up for wellness sessions designed to boost your energy and wellbeing',
      type: 'welcome',
      icon: '🧘',
      validate: () => '',
    },
    {
      id: 'personal',
      title: 'Tell us about yourself',
      subtitle: "Let's start with the basics",
      type: 'personal',
      icon: '👤',
      fields: ['name', 'email', 'phone', 'gender'],
      validate: () => {
        const nameError = validateName(formData.name)
        if (nameError) return { field: 'name', error: nameError }
        const emailError = validateEmail(formData.email)
        if (emailError) return { field: 'email', error: emailError }
        const phoneError = validatePhone(formData.phone)
        if (phoneError) return { field: 'phone', error: phoneError }
        const genderError = validateGender(formData.gender)
        if (genderError) return { field: 'gender', error: genderError }
        return null
      },
    },
    {
      id: 'experience',
      title: 'Yoga & Mindfulness Experience',
      subtitle: 'What is your experience level with yoga and mindfulness?',
      type: 'experience',
      icon: '🌿',
      field: 'yogaExperience',
      validate: () => validateExperience(formData.yogaExperience),
    },
    {
      id: 'wellness-scores',
      title: 'Your Current Wellness Scores',
      subtitle: 'Rate each aspect from 1 (lowest) to 10 (highest)',
      type: 'wellness-scores',
      icon: '📊',
      validate: validateWellnessScores,
    },
    {
      id: 'goals',
      title: 'Your Wellness Goal',
      subtitle: 'What would you like to achieve during this cohort?',
      type: 'goals',
      icon: '🎯',
      field: 'wellnessGoal',
      validate: () => validateGoal(formData.wellnessGoal),
    },
    {
      id: 'verify-email',
      title: 'Verify Your Email',
      subtitle: 'Enter the 4-digit code sent to your email',
      type: 'verify-email',
      icon: '✉️',
      validate: () => '',
    },
  ]

  const currentStepData = steps[currentStep]

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleNext = async () => {
    // Validate current step
    if (currentStepData.type !== 'welcome' && currentStepData.type !== 'verify-email') {
      const validationResult = currentStepData.validate()
      
      if (validationResult) {
        if (typeof validationResult === 'object') {
          setErrors((prev) => ({
            ...prev,
            [validationResult.field]: validationResult.error,
          }))
        } else {
          setErrors((prev) => ({
            ...prev,
            [currentStepData.field || 'general']: validationResult,
          }))
        }
        return
      }
    }

    // If we're at the goals step (second to last), submit and move to verification
    if (currentStepData.type === 'goals') {
      await handleSubmit()
      return
    }

    // If we're at the email verification step, do nothing (OTP input handles it)
    if (currentStepData.type === 'verify-email') {
      return
    }

    if (currentStep < steps.length - 2) { // -2 because we don't want to manually go to verify-email step
      setDirection(1)
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setErrors({})

    try {
      // Use new unified API endpoint
      const response = await api.post(API_ENDPOINTS.PROGRAMS.ENROLL(programSlug), formData)

      if (response.data.success) {
        const { user, enrollment } = response.data.data
        const newUserId = user.id
        setSignupId(newUserId)
        trackEvent('Sign Up', {
          signup_method: 'email',
          email: formData.email,
        })

        // Store user data temporarily (without token - will get token after verification)
        localStorage.setItem('pendingUserId', newUserId)
        localStorage.setItem('pendingUserData', JSON.stringify({ user, enrollment }))

        // Send verification OTP
        try {
          const verifyResponse = await api.post('/api/auth/send-verification', {
            userId: newUserId,
            programSlug,
          })

          if (verifyResponse.data.success) {
            setVerificationSent(true)
            setResendCooldown(30)
            // In development, store the OTP code for testing
            if (verifyResponse.data.data.code) {
              setDevCode(verifyResponse.data.data.code)
            }
          }
        } catch (verifyError) {
          console.error('Verification OTP error:', verifyError)
          // Still move to verification step, but show error
          setVerificationSent(true)
          setErrors({
            general: 'Signup successful, but we had trouble sending the verification code. Please try resending.',
          })
        }

        // Move to email verification step
        setDirection(1)
        setCurrentStep(steps.length - 1)
      }
    } catch (error) {
      console.error('Signup error:', error)
      setErrors({
        general: error.response?.data?.message || 'An error occurred. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setErrors({})

    // Auto-advance to next input
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus()
    }

    // Auto-submit when all digits are entered
    if (value && index === 3 && newOtp.every(d => d !== '')) {
      handleOtpSubmit(newOtp.join(''))
    }
  }

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 4)
    if (/^\d{1,4}$/.test(pastedData)) {
      const digits = pastedData.split('')
      const newOtp = [...otp]
      digits.forEach((digit, i) => {
        if (i < 4) newOtp[i] = digit
      })
      setOtp(newOtp)
      
      // Focus on the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex(d => d === '')
      if (nextEmptyIndex !== -1) {
        otpRefs[nextEmptyIndex].current?.focus()
      } else {
        otpRefs[3].current?.focus()
        // Auto-submit if all filled
        handleOtpSubmit(newOtp.join(''))
      }
    }
  }

  const handleOtpSubmit = async (code = otp.join('')) => {
    if (code.length !== 4) {
      setErrors({ general: 'Please enter the complete 4-digit code' })
      return
    }

    setIsSubmitting(true)
    setErrors({})

    try {
      const response = await api.post('/api/auth/verify-otp', {
        email: formData.email.trim(),
        code,
        programSlug,
      })

      if (response.data.success) {
        const { token } = response.data.data

        // Store auth token
        saveAuth(token, null)
        identifyUser(signupId, {
          '$name': formData.name,
          '$email': formData.email,
        })
        trackEvent('Sign In', {
          login_method: 'email',
          success: true,
        })

        // Clear pending user data
        localStorage.removeItem('pendingUserId')
        localStorage.removeItem('pendingUserData')

        // Navigate to dashboard
        navigate(ROUTES.COHORT_DASHBOARD(programSlug))
      }
    } catch (err) {
      console.error('Error verifying OTP:', err)
      setErrors({ general: err.response?.data?.message || 'Invalid or expired code. Please try again.' })
      setOtp(['', '', '', ''])
      otpRefs[0].current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const resendVerificationOTP = async () => {
    if (!signupId || resendCooldown > 0) return

    setIsSubmitting(true)
    setErrors({})
    setOtp(['', '', '', ''])

    try {
      const response = await api.post('/api/auth/send-verification', {
        userId: signupId,
        programSlug,
      })

      if (response.data.success) {
        setVerificationSent(true)
        setResendCooldown(30)
        if (response.data.data.code) {
          setDevCode(response.data.data.code)
        }
        otpRefs[0].current?.focus()
      }
    } catch (error) {
      console.error('Resend verification error:', error)
      setErrors({
        general: error.response?.data?.message || 'Failed to resend verification code. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNext()
    }
  }

  // Animation variants
  const pageVariants = {
    initial: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8,
    }),
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.43, 0.13, 0.23, 0.96],
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
      },
    }),
  }

  // Render personal info step
  const renderPersonalInfo = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter your full name"
          className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
            errors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}
        />
        {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="your.email@example.com"
          className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
            errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}
        />
        {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="+91 9XXXXXXXXX"
          className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
            errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}
        />
        {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
        <select
          value={formData.gender}
          onChange={(e) => handleInputChange('gender', e.target.value)}
          className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
            errors.gender ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          {genderOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.gender && <p className="text-red-600 text-sm mt-1">{errors.gender}</p>}
      </div>
    </div>
  )

  // Render experience step with card selection
  const renderExperience = () => (
    <div className="grid grid-cols-2 gap-4">
      {experienceOptions.map((option) => (
        <motion.div
          key={option.value}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <label
            className={`flex flex-col items-center p-6 rounded-2xl cursor-pointer border-2 transition-all text-center h-full ${
              formData.yogaExperience === option.value
                ? 'border-primary-600 bg-primary-50 shadow-lg'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow-md'
            }`}
          >
            <input
              type="radio"
              name="yogaExperience"
              value={option.value}
              checked={formData.yogaExperience === option.value}
              onChange={(e) => handleInputChange('yogaExperience', e.target.value)}
              className="sr-only"
            />
            <span className="text-4xl mb-3">{option.emoji}</span>
            <span className={`text-lg font-semibold mb-1 ${
              formData.yogaExperience === option.value ? 'text-primary-700' : 'text-gray-800'
            }`}>
              {option.label}
            </span>
            <span className="text-sm text-gray-500">{option.description}</span>
          </label>
        </motion.div>
      ))}
      {errors.yogaExperience && (
        <p className="text-red-600 text-sm text-center col-span-2">{errors.yogaExperience}</p>
      )}
    </div>
  )

  // Render wellness scores step
  const renderWellnessScores = () => (
    <div className="space-y-8">
      {WELLNESS_CHECK_IN_SCORES.map((score) => (
        <WellnessScoreQuestion
          key={score.key}
          score={score}
          value={formData[score.key]}
          onChange={(rating) => handleInputChange(score.key, rating)}
          error={errors[score.key]}
          variant="primary"
        />
      ))}
      {errors.general && (
        <p className="text-red-600 text-sm text-center">{errors.general}</p>
      )}
    </div>
  )

  // Render goals step
  const renderGoals = () => (
    <div className="space-y-3">
      {goalOptions.map((option) => (
        <motion.div
          key={option.value}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <label
            className={`flex items-center p-4 rounded-xl cursor-pointer border-2 transition-all ${
              formData.wellnessGoal === option.value
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="wellnessGoal"
              value={option.value}
              checked={formData.wellnessGoal === option.value}
              onChange={(e) => handleInputChange('wellnessGoal', e.target.value)}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                formData.wellnessGoal === option.value
                  ? 'border-primary-600 bg-primary-600'
                  : 'border-gray-300'
              }`}
            >
              {formData.wellnessGoal === option.value && (
                <div className="w-2 h-2 bg-white rounded-full" />
              )}
            </div>
            <span className="text-lg">{option.label}</span>
          </label>
        </motion.div>
      ))}
      {errors.wellnessGoal && (
        <p className="text-red-600 text-sm text-center">{errors.wellnessGoal}</p>
      )}
    </div>
  )

  // Render email verification step with OTP input
  const renderEmailVerification = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center space-y-6"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto"
      >
        <svg
          className="w-10 h-10 text-primary-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </motion.div>

      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-gray-900">
          Check your email!
        </h3>
        <p className="text-gray-600">
          We've sent a verification code to:
        </p>
        <p className="text-primary-600 font-medium text-lg">
          {formData.email}
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center gap-3 py-4">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={otpRefs[index]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            onPaste={index === 0 ? handleOtpPaste : undefined}
            className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
              errors.general ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
            disabled={isSubmitting}
          />
        ))}
      </div>

      {errors.general && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-sm text-center ${errors.general.includes('sent') ? 'text-green-600' : 'text-red-600'}`}
        >
          {errors.general}
        </motion.p>
      )}

      {/* Development mode: show OTP code */}
      {import.meta.env.VITE_SHOW_DEV_LINKS === 'true' && devCode && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-sm text-yellow-800">
            Dev Mode - Code: <span className="font-bold">{devCode}</span>
          </p>
        </div>
      )}

      <button
        onClick={() => handleOtpSubmit()}
        disabled={isSubmitting || otp.some(d => d === '')}
        className="w-full py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
            />
            <span>Verifying...</span>
          </>
        ) : (
          'Verify & Continue'
        )}
      </button>

      {/* Pending check-ins notice */}
      {hasPendingCheckIns && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="text-sm text-purple-800">
            <span className="font-medium">Good news!</span> Your {pendingCheckIns.length} pending check-in{pendingCheckIns.length > 1 ? 's' : ''} will be saved automatically after you verify.
          </p>
        </div>
      )}

      <div className="pt-2 space-y-2">
        <button
          onClick={resendVerificationOTP}
          disabled={resendCooldown > 0 || isSubmitting}
          className="text-primary-600 hover:text-primary-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resendCooldown > 0 
            ? `Resend code in ${resendCooldown}s`
            : "Didn't receive the code? Resend"
          }
        </button>
      </div>
    </motion.div>
  )

  // Render step content based on type
  const renderStepContent = () => {
    switch (currentStepData.type) {
      case 'personal':
        return renderPersonalInfo()
      case 'experience':
        return renderExperience()
      case 'wellness-scores':
        return renderWellnessScores()
      case 'goals':
        return renderGoals()
      case 'verify-email':
        return renderEmailVerification()
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <img
            src="/logo.png"
            alt="Wellness Loop Logo"
            className="h-16 w-auto"
          />
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-primary-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-white/50 rounded-full overflow-hidden backdrop-blur-sm">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-600 to-purple-600 origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: (currentStep + 1) / steps.length }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              {/* Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                  delay: 0.2,
                }}
                className="text-5xl text-center"
              >
                {currentStepData.icon}
              </motion.div>

              {/* Title and subtitle */}
              <div className="text-center space-y-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {currentStepData.title}
                </h2>
                <p className="text-lg text-gray-600">{currentStepData.subtitle}</p>
              </div>

              {/* Step content */}
              {currentStepData.type !== 'welcome' && renderStepContent()}

              {/* Error message (only show for non-verification steps) */}
              {errors.general && currentStepData.type !== 'verify-email' && (
                <p className="text-red-600 text-sm text-center">{errors.general}</p>
              )}

              {/* Navigation buttons (hide on verification step) */}
              {currentStepData.type !== 'verify-email' && (
                <div className="flex gap-4 pt-4">
                  {currentStep > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1 py-4 text-lg"
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    className={`py-4 text-lg ${currentStep === 0 ? 'w-full' : 'flex-1'}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Submitting...'
                      : currentStep === 0
                      ? 'Start Signup'
                      : currentStepData.type === 'goals'
                      ? 'Complete & Verify Email'
                      : 'Continue'}
                  </Button>
                </div>
              )}

              {/* Step indicators */}
              <div className="flex justify-center gap-2 pt-4">
                {steps.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'w-8 bg-primary-600'
                        : index < currentStep
                        ? 'w-2 bg-primary-400'
                        : 'w-2 bg-gray-300'
                    }`}
                    initial={false}
                    animate={{
                      scale: index === currentStep ? 1.2 : 1,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  )
}

export default CohortSignup
